'use client';
import React, { FC } from 'react';
import { TypeAddToCartSectionFields, TypeImage } from '@/core/generated-types';
import { Image } from '@/components/ui/Image';
import { Badge } from '@/components/ui';
import {
  getProductInventoryStatus,
  ShopifyProduct,
  ShopifyProductType,
} from '@/lib/shopify';
import { getDefaultVariant } from '@/utils/shopify';
import { API_URLS } from '@/lib/api/config';
import { CartItemProperty, ITEM_ORIGINS } from '@/core/constants';
import { AddButton } from '../InventoryStatus';
import { usePathname } from '@/navigation';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';
import { formatMoney } from '@/utils/helpers';
import useLocale from '@/hooks/useLocale';
import { useProductValidations } from '@/hooks/useProductValidations';
import { PRODUCT_ADDED_COMPONENT_TYPES } from '@/utils/rudderstack';
import { handleCartFormSubmit } from '@/utils/shopify/handleCartFormSubmit';

const AddToCartSection: FC<TypeAddToCartSectionFields> = ({
  backgroundColor,
  media,
  heading,
  introText,
  badge,
  product,
  loginPrompt,
}) => {
  const locale = useLocale();
  const pathname = usePathname();
  const productFields = product.fields;
  const shopifyProduct = (product.fields as any)
    .shopifyProduct as ShopifyProduct;

  const defaultVariant = getDefaultVariant(
    product.fields.title || '',
    shopifyProduct?.variants ?? [],
  );

  const isValidProduct = useProductValidations(product);

  const isSkillSet =
    shopifyProduct?.productType === ShopifyProductType.SKILL_SET;

  const inventoryStatus =
    shopifyProduct && defaultVariant
      ? getProductInventoryStatus(shopifyProduct, defaultVariant)
      : null;

  const bg = backgroundColor?.fields?.color;
  const bgClass = bg ? `bg-${bg}` : 'bg-blue-light';

  return defaultVariant ? (
    <div className="px-8 py-12">
      {heading && (
        <div className="mb-4 text-center text-[24px] font-bold md:text-[32px]">
          {heading}
        </div>
      )}
      {introText && (
        <div className="text-center text-base leading-[27px] md:text-[20px] mb-8">
          {introText}
        </div>
      )}
      <div className="max-w-[1000px] mx-auto md:flex flex-none  drop-shadow-md">
        <div className="w-full md:w-1/2">
          <Image
            alt={media?.fields?.altText}
            className="h-full w-full object-cover"
            imageData={media as TypeImage}
          />
        </div>
        <div
          className={`${bgClass} p-6 md:p-12 w-full md:w-1/2 flex flex-col justify-center space-y-5`}
        >
          <div className="">{badge && <Badge {...badge.fields} />}</div>
          <div className="text-2xl font-bold">{productFields.title}</div>
          {productFields.shortDescription && (
            <div className="">{productFields.shortDescription}</div>
          )}
          <div className="text-[32px] font-bold text-blue-deep">
            {defaultVariant.price?.amount &&
              formatMoney(Number(defaultVariant.price.amount), locale, true)}
            {productFields.strikethroughPrice && (
              <span className="text-lg line-through font-normal text-[#6E757C] ml-3">
                {formatMoney(productFields.strikethroughPrice, locale, true)}
              </span>
            )}
          </div>

          <form
            id="add-to-cart-form"
            action={`${API_URLS.CART_SERVICE}/add`}
            method="post"
            onSubmit={(e) =>
              handleCartFormSubmit({
                e,
                shopifyProduct,
                shopifyVariant: defaultVariant,
                productFields: productFields,
                url: pathname,
                defaultQuantity: 1,
                buttonLevel:
                  PRODUCT_ADDED_COMPONENT_TYPES.FULL_WIDTH_PRODUCT_CARD,
              })
            }
          >
            <input
              type="hidden"
              name="lines"
              value={JSON.stringify([
                {
                  merchandiseId: defaultVariant.id,
                  quantity: 1,
                  attributes: [
                    {
                      key: CartItemProperty.ItemOrigin,
                      value: ITEM_ORIGINS.PRODUCT_PAGE,
                    },
                    {
                      key: CartItemProperty.IsSkillSet,
                      value: String(isSkillSet),
                    },
                  ],
                },
              ])}
            />
            {inventoryStatus && (
              <div className="md:max-w-[360px]">
                <AddButton
                  inventoryStatus={inventoryStatus}
                  product={product}
                />
              </div>
            )}
          </form>
        </div>
      </div>
      {!isValidProduct && (
        <div className="text-sm mt-4 text-center" data-testid="login-prompt">
          <RichText content={loginPrompt as Document} openLinkSameWindow />
        </div>
      )}
    </div>
  ) : null;
};

export default React.memo(AddToCartSection);
